import React, { type FC, useCallback, useRef, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    MagnifyingGlassIcon,
    PlusIcon, TrashIcon, UserPlusIcon, CubeIcon, TruckIcon, ShoppingCartIcon,
} from '@heroicons/react/24/outline';
import { EllipsisVerticalIcon, EyeIcon } from '@heroicons/react/24/solid';

import type { Customer, Address, OrderFormData, CustomerWithoutId, Order, DeliveryFormData } from '../types';
import { useOrders, useOrdersPageRealtimeRefresh } from '../hooks/useOrders';
import { useCustomers } from '../hooks/useCustomers';
import { useStores } from '../hooks/useStores';
import CreateOrderModal from '../components/CreateOrderModal';
import CreateDeliveryModal from '../components/CreateDeliveryModal';
import StatusBadge from '../components/StatusBadge';
import { supabase } from '../clients/supabaseClient';
import { geocodeAddress } from '../lib/geocode';
import OrderTypeBadge from "../components/OrderTypeBadge.tsx";
import TablePagination from "../components/TablePagination.tsx";
import {useToast} from "../hooks/useToast.ts";


interface OrdersProps { title?: string }

const ITEMS_PER_PAGE = 25;

const STATUS_OPTIONS = [
    { value: 'CREATED',              label: 'Created' },
    { value: 'AWAITING_PAYMENT',     label: 'Awaiting Payment' },
    { value: 'AWAITING_PACKING',     label: 'Awaiting Packing' },
    { value: 'PAYMENT_FAILED',       label: 'Payment Failed' },
    { value: 'PAYMENT_CANCELLED',    label: 'Payment Cancelled' },
    { value: 'PACKER_ASSIGNED',      label: 'Packer Assigned' },
    { value: 'READY_FOR_COLLECTION', label: 'Ready for Collection' },
    { value: 'COLLECTION_COMPLETE',  label: 'Collected' },
    { value: 'ASSIGN_DRIVER',        label: 'Assign Driver' },
    { value: 'DRIVER_ASSIGNED',      label: 'Driver Assigned' },
    { value: 'ON_ROUTE',             label: 'On Route' },
    { value: 'DELIVERY_COMPLETE',    label: 'Delivered' },
    { value: 'DELIVERY_FAILED',      label: 'Failed' },
    { value: 'ORDER_CANCELLED',      label: 'Cancelled' },
];

const getActionsByStatus = (status: string): Array<{
    label: string;
    icon: React.ComponentType<{ className?: string }>;
    tab: 'overview' | 'details' | 'payments' | 'deliveries' | 'logs';
}> => {
    const s = status?.toUpperCase();
    const actions: Array<{ label: string; icon: React.ComponentType<{ className?: string }>; tab: 'overview' | 'details' | 'payments' | 'deliveries' | 'logs' }> = [];
    switch (s) {
        case 'AWAITING_PACKING':
            actions.push({ label: 'Assign Packer', icon: UserPlusIcon, tab: 'details' }); break;
        case 'PACKER_ASSIGNED':
            actions.push({ label: 'Complete Packing', icon: CubeIcon, tab: 'details' }); break;
        case 'READY_FOR_COLLECTION':
            actions.push({ label: 'View Collection', icon: CubeIcon, tab: 'overview' }); break;
        case 'PACKING_COMPLETE':
        case 'ASSIGN_DRIVER':
        case 'DELIVERY_FAILED':
            actions.push({ label: 'Assign Driver', icon: TruckIcon, tab: 'details' }); break;
        case 'DRIVER_ASSIGNED':
        case 'ON_ROUTE':
            actions.push({ label: 'Track Delivery', icon: TruckIcon, tab: 'deliveries' }); break;
    }
    return actions;
};

const NON_CANCELLABLE_STATUSES = ['ON_ROUTE', 'DELIVERY_COMPLETE', 'DELIVERY_FAILED', 'ORDER_CANCELLED', 'COLLECTION_COMPLETE'];

const Orders: FC<OrdersProps> = () => {
    const {
        fetchOrdersPage,
        createOrder,
        updateOrderStatus,
        assignDriverToOrder,
        loading: orderLoading,
    } = useOrders();
    const { customers, fetchCustomers, createCustomer, createAddress, setCustomersPrimaryAddress, fetchAddressesForCustomer } = useCustomers();
    const [addresses, setAddresses] = useState<Address[]>([]);
    const { stores, fetchStores } = useStores();

    const navigate   = useNavigate();
    const location   = useLocation();
    const { error, success } = useToast();

    const statusDropdownRef  = useRef<HTMLDivElement | null>(null);
    const actionButtonRefs   = useRef<Record<string | number, HTMLButtonElement | null>>({});
    const actionMenuRefs     = useRef<Record<string | number, HTMLDivElement | null>>({});
    const [actionMenuPosition, setActionMenuPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [customersLoaded, setCustomersLoaded] = useState(false);
    const [storesLoaded, setStoresLoaded] = useState(false);
    const [dropdownOpenId, setDropdownOpenId] = useState<string | number | null>(null);
    const [showNewCustomerForm, setShowNewCustomerForm] = useState(false);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilters, setStatusFilters] = useState<string[]>(() => {
        const incoming = (location.state)?.statuses;
        if (Array.isArray(incoming) && incoming.length > 0) return incoming.map((s: string) => String(s).toUpperCase());
        return [];
    });
    const [typeFilter, setTypeFilter] = useState<string>('All');
    const [deliveryTypeFilter, setDeliveryTypeFilter] = useState<string>('All');
    const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
    const [activeTab, setActiveTab] = useState<'open' | 'delivered'>('open');
    const [currentPage, setCurrentPage] = useState(1);
    const [ordersPageRows, setOrdersPageRows] = useState<Order[] | null>(null);
    const [ordersTotalCount, setOrdersTotalCount] = useState(0);
    const [isCreateDeliveryModalOpen, setIsCreateDeliveryModalOpen] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
    const [newDelivery, setNewDelivery] = useState<DeliveryFormData>({ destination_id: '', driver_id: '' });
    const [orderData, setOrderData] = useState<OrderFormData>({
        reference: '', parcel_count: 1, customer_id: '', delivery_address_id: '',
        type: '', notes: '', store_id: '', delivery_fee: 0, delivery_type: '', collection_point_id: '',
    });
    const [newCustomer, setNewCustomer] = useState<CustomerWithoutId>({
        first_name: '', last_name: '', cellphone: '', delivery_address: null,
        address: { address_1: '', address_2: '', suburb: '', city: '', postcode: '' },
    });

    const getMenuPosition = (button: HTMLButtonElement) => {
        const rect = button.getBoundingClientRect();
        const menuWidth = 192;
        return {
            top:  rect.bottom + 8,
            left: Math.max(8, Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 8)),
        };
    };

    const loadOrdersPage = useCallback(async () => {
        setOrdersPageRows(null);
        const result = await fetchOrdersPage({
            page: currentPage, pageSize: ITEMS_PER_PAGE, activeTab,
            statuses: statusFilters, type: typeFilter,
            deliveryType: deliveryTypeFilter, search: searchQuery,
        });
        setOrdersPageRows(result?.rows ?? []);
        setOrdersTotalCount(result?.count ?? 0);
    }, [fetchOrdersPage, currentPage, activeTab, statusFilters, typeFilter, deliveryTypeFilter, searchQuery]);

    useEffect(() => {
        void loadOrdersPage();
    }, [loadOrdersPage]);

    useOrdersPageRealtimeRefresh(loadOrdersPage);

    useEffect(() => {
        if (location.state?.openCreate) setIsModalOpen(true);
        const incoming = (location.state)?.statuses;
        if (Array.isArray(incoming) && incoming.length > 0) {
            const arr = incoming.map((s: string) => String(s).toUpperCase());
            setStatusFilters(prev => {
                const isSame = prev.length === arr.length && prev.every((s, i) => s === arr[i]);
                return isSame ? prev : arr;
            });
        }
        if (location.state) window.history.replaceState({}, '');
    }, [location.state]);

    useEffect(() => {
        if (!isModalOpen) return;
        const load = async () => {
            await Promise.all([
                customersLoaded ? Promise.resolve() : fetchCustomers(),
                storesLoaded    ? Promise.resolve() : fetchStores(),
            ]);
            setCustomersLoaded(true);
            setStoresLoaded(true);
        };
        void load();
    }, [isModalOpen, customersLoaded, storesLoaded, fetchCustomers, fetchStores]);

    useEffect(() => { setCurrentPage(1); }, [searchQuery, statusFilters, typeFilter, deliveryTypeFilter, activeTab]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target as Node)) {
                setIsStatusDropdownOpen(false);
            }
            const targetNode = event.target as Node;
            if (dropdownOpenId !== null) {
                const activeButton = actionButtonRefs.current[dropdownOpenId];
                const activeMenu   = actionMenuRefs.current[dropdownOpenId];
                if (activeButton && !activeButton.contains(targetNode) && activeMenu && !activeMenu.contains(targetNode)) {
                    setDropdownOpenId(null);
                }
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [dropdownOpenId]);

    useEffect(() => {
        if (dropdownOpenId === null) return;
        const updatePosition = () => {
            const button = actionButtonRefs.current[dropdownOpenId];
            if (!button) return;
            const rect = button.getBoundingClientRect();
            setActionMenuPosition({
                top:  rect.bottom + 8,
                left: Math.max(8, Math.min(rect.right - 192, window.innerWidth - 200)),
            });
        };
        updatePosition();
        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition, true);
        return () => {
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
        };
    }, [dropdownOpenId]);

    const formatDate = (d?: string | null) => d ? new Date(d).toLocaleString() : '–';

    const handleOrderChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setOrderData(p => ({
            ...p,
            [name]: name === 'parcel_count' ? parseInt(value) || 0 : name === 'notes' ? value.slice(0, 1500) : value,
        }));
    };

    const handleOrderItemsChange = (items: OrderFormData['order_items']) => {
        setOrderData(p => ({ ...p, order_items: items }));
    };
    const isCustomerField = (f: string): f is keyof Omit<Customer, 'id' | 'address'> => ['first_name', 'last_name', 'cellphone'].includes(f);
    const isAddressField  = (f: string): f is keyof Address => ['address_1', 'address_2', 'suburb', 'city', 'postcode'].includes(f);

    const handleCustomerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        if (name.includes('.')) {
            const [parent, child] = name.split('.');
            if (parent === 'address' && isAddressField(child)) setNewCustomer(p => ({ ...p, address: { ...p.address, [child]: value } }));
        } else if (isCustomerField(name)) {
            setNewCustomer(p => ({ ...p, [name]: value }));
        }
    };

    const toggleStatusFilter = (s: string) => setStatusFilters(p => p.includes(s) ? p.filter(x => x !== s) : [...p, s]);
    const clearStatusFilters = () => setStatusFilters([]);

    const openAssignFor = async (order: Order) => {
        setSelectedOrder(order);
        const initialAddressId = order.delivery_address_id || order.customer?.delivery_address || null;
        setNewDelivery({ destination_id: initialAddressId, driver_id: null });
        if (order.customer_id) {
            const fetched = await fetchAddressesForCustomer(order.customer_id);
            setAddresses(fetched);
        }
        setIsCreateDeliveryModalOpen(true);
    };

    const handleDeliverySubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!selectedOrder) return;



        if (!newDelivery.destination_id || !newDelivery.driver_id) return;

        try {
            setLoading(true);

            const assigned = await assignDriverToOrder({
                orderId:       String(selectedOrder.id),
                customerId:    String(selectedOrder.customer_id),
                destinationId: newDelivery.destination_id,
                driverId:      newDelivery.driver_id,
            });

            if (assigned) {
                setIsCreateDeliveryModalOpen(false);
                await loadOrdersPage();
            }
        } finally {
            setLoading(false);
        }
    };

    const switchToOpenTab      = () => { setActiveTab('open');      setStatusFilters([]); setIsStatusDropdownOpen(false); };
    const switchToDeliveredTab = () => { setActiveTab('delivered'); setStatusFilters([]); setIsStatusDropdownOpen(false); };

    const statusFilterLabel =
        statusFilters.length === 0 ? 'All Statuses'
            : statusFilters.length === 1 ? STATUS_OPTIONS.find(s => s.value === statusFilters[0])?.label ?? '1 selected'
                : `${statusFilters.length} statuses selected`;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        try {
            let newOrderCustomerId: string | number | undefined;
            let newOrderDeliveryAddressId: string | null = null;

            if (!orderData.customer_id && newCustomer.first_name && newCustomer.last_name) {
                const createdCustomer = await createCustomer({
                    first_name: newCustomer.first_name,
                    last_name:  newCustomer.last_name,
                    cellphone:  newCustomer.cellphone,
                    delivery_address: null,
                });
                if (!createdCustomer?.id) {
                    error('Failed to create customer');
                    return;
                }
                newOrderCustomerId = createdCustomer.id;

                const queryLine = [newCustomer.address.address_1, newCustomer.address.suburb, newCustomer.address.city].filter(Boolean).join(', ');
                const coords = await geocodeAddress(queryLine);

                const createdAddress = await createAddress({
                    ...newCustomer.address,
                    customer_id: createdCustomer.id,
                    latitude:  coords ? String(coords.lat) : null,
                    longitude: coords ? String(coords.lng) : null,
                });
                if (!createdAddress?.id) {
                    error('Failed to create customer address');
                    return;
                }
                newOrderDeliveryAddressId = createdAddress.id;
                await setCustomersPrimaryAddress(createdCustomer.id, createdAddress.id);
            } else {
                newOrderCustomerId        = orderData.customer_id;
                newOrderDeliveryAddressId = orderData.delivery_address_id || null;
            }

            if (!newOrderCustomerId) return;

            const orderItemsTotal = (orderData.order_items || []).reduce((sum, item) => sum + (item.unit_price * item.quantity), 0);

            const createdOrder = await createOrder({
                reference:           orderData.reference,
                parcel_count:        orderData.parcel_count,
                customer_id:         newOrderCustomerId,
                delivery_address_id: newOrderDeliveryAddressId,
                type:                'chat',
                notes:               (orderData.notes || '').slice(0, 1500),
                store_id:            orderData.store_id || null,
                total:               orderItemsTotal,
                delivery_fee:        orderData.delivery_fee,
                delivery_type:       orderData.delivery_type,
                collection_point_id: orderData.collection_point_id || null,
            } as OrderFormData);

            if (createdOrder?.id) {
                if (orderData.order_items && orderData.order_items.length > 0) {
                    for (const item of orderData.order_items) {
                        let priceRecordId = item.product_prices_id;
                        if (!priceRecordId) {
                            const { data: priceRecord, error: priceError } = await supabase
                                .from('product_prices').select('id, price')
                                .eq('product_id', item.product_id)
                                .eq('store_id', orderData.store_id).single();
                            if (priceError || !priceRecord) {
                                error(`Price not found for ${item.product_name} at this store.`);
                                continue;
                            }
                            priceRecordId = priceRecord.id;
                        }
                        const { error: itemError } = await supabase.from('order_items').insert({
                            order_id:          createdOrder.id,
                            product_id:        item.product_id,
                            product_prices_id: priceRecordId,
                            quantity:          item.quantity,
                        });
                        if (itemError) error(`Failed to add ${item.product_name} to order`);
                    }
                }
                await updateOrderStatus(String(createdOrder.id), 'AWAITING_PACKING');
                success('Order created successfully!');
            }
            handleCloseModal();
        } catch (err) {
            error(err instanceof Error ? err.message : 'Failed to create order');
        } finally {
            await loadOrdersPage();
            setLoading(false);
        }
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setShowNewCustomerForm(false);
        setOrderData({ reference: '', parcel_count: 1, customer_id: '', delivery_address_id: '', type: '', notes: '', store_id: '', delivery_fee: 0, delivery_type: '', collection_point_id: '' });
        setNewCustomer({ first_name: '', last_name: '', cellphone: '', delivery_address: null, address: { address_1: '', address_2: '', suburb: '', city: '', postcode: '' } });
    };

    const goToOrder = (id: string | number) => navigate(`/orders/${id}`);
    const totalItems  = ordersTotalCount;
    const totalPages  = Math.max(1, Math.ceil(totalItems / ITEMS_PER_PAGE));
    const paginatedOrders = ordersPageRows ?? [];

    const handlePageChange = (newPage: number) => {
        if (newPage >= 1 && newPage <= totalPages) { setCurrentPage(newPage); window.scrollTo({ top: 0, behavior: 'smooth' }); }
    };

    // ── Render ─────────────────────────────────────────────────────────────────
    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-semibold text-stone-900">Orders</h1>
                <p className="mt-1 text-sm text-stone-500">Monitor and manage customer orders across all channels</p>
            </div>

            {/* Tabs */}
            <div className="flex">
                <div className="inline-flex overflow-hidden rounded-lg border border-stone-300 bg-white shadow-sm">
                    <button type="button" onClick={switchToOpenTab}
                            className={`px-4 py-2 text-sm font-medium transition-colors ${activeTab === 'open' ? 'bg-stone-900 text-white' : 'text-stone-700 hover:bg-stone-50'}`}>
                        Open
                    </button>
                    <button type="button" onClick={switchToDeliveredTab}
                            className={`px-4 py-2 text-sm font-medium transition-colors ${activeTab === 'delivered' ? 'bg-stone-900 text-white' : 'text-stone-700 hover:bg-stone-50'}`}>
                        Delivered
                    </button>
                </div>
            </div>

            {/* Filters */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:flex-wrap">
                <div className="relative min-w-[280px] flex-1">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
                    <input type="text" placeholder="Search by order ref or customer name…" value={searchQuery}
                           onChange={e => setSearchQuery(e.target.value)}
                           className="w-full rounded-lg border border-stone-300 bg-white py-2.5 pl-10 pr-4 text-sm shadow-sm placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-[#ee2b74]" />
                </div>
                <div className="relative w-full sm:w-48">
                    <select value={deliveryTypeFilter} onChange={e => setDeliveryTypeFilter(e.target.value)}
                            className="block w-full appearance-none rounded-lg border border-stone-300 bg-white px-4 py-2.5 pr-10 text-sm text-stone-700 shadow-sm outline-none focus:ring-2 focus:ring-[#ee2b74]">
                        <option value="All">All Fulfilment</option>
                        <option value="delivery">🚚 Delivery</option>
                        <option value="collection">🏪 Collection</option>
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-stone-400">
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                    </div>
                </div>
                <div className="relative w-full sm:w-40">
                    <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
                            className="block w-full appearance-none rounded-lg border border-stone-300 bg-white px-4 py-2.5 pr-10 text-sm text-stone-700 shadow-sm outline-none focus:ring-2 focus:ring-[#ee2b74]">
                        <option value="All">All Types</option>
                        <option value="ecom">Ecom Orders</option>
                        <option value="chat">Chat Orders</option>
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-stone-400">
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                    </div>
                </div>
                <div ref={statusDropdownRef} className="relative">
                    <button onClick={() => setIsStatusDropdownOpen(p => !p)}
                            className="flex items-center gap-2 rounded-lg border border-stone-300 bg-white px-4 py-2.5 text-sm text-stone-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-[#ee2b74]">
                        <span>{statusFilterLabel}</span>
                        <svg className="h-4 w-4 text-stone-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                    </button>
                    {isStatusDropdownOpen && (
                        <div className="absolute left-0 z-20 mt-2 w-60 rounded-xl border border-stone-200 bg-white shadow-lg">
                            <div className="flex items-center justify-between border-b border-stone-100 px-3 py-2">
                                <span className="text-xs font-semibold uppercase tracking-wide text-stone-500">Filter by status</span>
                                <button onClick={clearStatusFilters} className="text-xs font-medium text-[#ee2b74] hover:underline">Clear all</button>
                            </div>
                            <div className="max-h-64 overflow-y-auto py-2">
                                {STATUS_OPTIONS.map(opt => (
                                    <label key={opt.value} className="flex cursor-pointer items-center gap-3 px-3 py-2 text-sm text-stone-700 hover:bg-stone-50">
                                        <input type="checkbox" checked={statusFilters.includes(opt.value)} onChange={() => toggleStatusFilter(opt.value)}
                                               className="h-4 w-4 rounded border-stone-300 accent-[#ee2b74]" />
                                        {opt.label}
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
                <button onClick={() => setIsModalOpen(true)}
                        className="inline-flex items-center gap-2 whitespace-nowrap rounded-lg bg-[#ee2b74] px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-[#d4245f] active:scale-95 sm:ml-auto">
                    <PlusIcon className="h-4 w-4" /> Capture New Order
                </button>
            </div>

            {/* Table */}
            <div className="overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm">
                {ordersPageRows === null ? (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="border-b border-stone-200 bg-stone-50">
                            <tr>{['Order Ref','Customer','Type / Fulfilment','Status','Created','Action'].map(h => (
                                <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">{h}</th>
                            ))}</tr>
                            </thead>
                            <tbody className="divide-y divide-stone-100">
                            {Array.from({ length: 8 }).map((_, i) => (
                                <tr key={i} className="animate-pulse">
                                    <td className="px-4 py-3"><div className="h-4 w-28 bg-stone-100 rounded" /></td>
                                    <td className="px-4 py-3"><div className="h-4 w-32 bg-stone-100 rounded" /></td>
                                    <td className="px-4 py-3"><div className="h-5 w-24 bg-stone-100 rounded-full" /></td>
                                    <td className="px-4 py-3"><div className="h-5 w-28 bg-stone-100 rounded-full" /></td>
                                    <td className="px-4 py-3"><div className="h-4 w-24 bg-stone-100 rounded" /></td>
                                    <td className="px-4 py-3"><div className="h-7 w-7 bg-stone-100 rounded ml-auto" /></td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                ) : paginatedOrders.length === 0 ? (
                    <div className="px-4 py-10 text-center text-sm text-stone-500">
                        {activeTab === 'delivered' ? 'No delivered orders found.' : 'No orders found.'}
                    </div>
                ) : (
                    <>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="border-b border-stone-200 bg-stone-50">
                                <tr>
                                    {['Order Ref','Customer','Type / Fulfilment','Status','Created','Action'].map(h => (
                                        <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">{h}</th>
                                    ))}
                                </tr>
                                </thead>
                                <tbody className="divide-y divide-stone-100">
                                {paginatedOrders.map(order => (
                                    <tr key={order.id} onClick={() => goToOrder(order.id)}
                                        className="cursor-pointer transition hover:bg-stone-50">
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-2">
                                                <span className="font-medium text-stone-900">{order.reference}</span>
                                                {order.shared_basket_id && (
                                                    <span title="Shared Basket"
                                                          className="inline-flex items-center gap-1 rounded-md bg-pink-50 border border-pink-100 px-1.5 py-0.5">
                                                            <ShoppingCartIcon className="h-3 w-3 text-[#ee2b74]" />
                                                        </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-stone-700">
                                            {order.customer?.first_name} {order.customer?.last_name}
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-1.5 flex-wrap">
                                                <OrderTypeBadge type={order.type} />
                                                {order.delivery_type && (
                                                    <span className={`rounded-md border px-2 py-0.5 text-xs capitalize ${order.delivery_type === 'collection' ? 'border-teal-100 bg-teal-50 text-teal-700' : 'border-sky-100 bg-sky-50 text-sky-700'}`}>
                                                            {order.delivery_type === 'collection' ? 'collection' : 'delivery'}
                                                        </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3"><StatusBadge status={order.status} /></td>
                                        <td className="px-4 py-3 text-stone-500 text-xs">{formatDate(order.created_at)}</td>
                                        <td className="px-4 py-3 text-right overflow-visible">
                                            <button
                                                ref={el => { actionButtonRefs.current[order.id] = el; }}
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    setActionMenuPosition(getMenuPosition(e.currentTarget));
                                                    setDropdownOpenId(prev => prev === order.id ? null : order.id);
                                                }}
                                                className="rounded-md p-1 transition-colors hover:bg-stone-200" type="button">
                                                <EllipsisVerticalIcon className="h-5 w-5 text-stone-500" />
                                            </button>
                                            {dropdownOpenId === order.id && createPortal(
                                                <div
                                                    ref={el => { actionMenuRefs.current[order.id] = el; }}
                                                    className="fixed z-[9999] w-48 rounded-xl border border-stone-200 bg-white py-2 shadow-xl divide-y divide-stone-100"
                                                    style={{ top: actionMenuPosition.top, left: actionMenuPosition.left }}>
                                                    <button type="button"
                                                            onClick={e => { e.stopPropagation(); goToOrder(order.id); setDropdownOpenId(null); }}
                                                            className="flex w-full items-center gap-3 px-4 py-2 text-left text-sm text-stone-700 hover:bg-stone-50">
                                                        <EyeIcon className="h-4 w-4 text-stone-400" /> View Details
                                                    </button>
                                                    {getActionsByStatus(order.status).map((action, i) => (
                                                        <button key={i} type="button"
                                                                onClick={e => {
                                                                    e.stopPropagation();
                                                                    if (action.label === 'Assign Driver') { void openAssignFor(order); }
                                                                    else { navigate(`/orders/${order.id}`, { state: { activeTab: action.tab } }); }
                                                                    setDropdownOpenId(null);
                                                                }}
                                                                className="flex w-full items-center gap-3 px-4 py-2 text-left text-sm text-[#ee2b74] hover:bg-pink-50">
                                                            <action.icon className="h-4 w-4" /> {action.label}
                                                        </button>
                                                    ))}
                                                    {!NON_CANCELLABLE_STATUSES.includes(order.status?.toUpperCase()) && (
                                                        <button type="button"
                                                                onClick={async e => {
                                                                    e.stopPropagation();
                                                                    if (window.confirm('Are you sure you want to cancel this order?')) {
                                                                        await updateOrderStatus(String(order.id), 'ORDER_CANCELLED');
                                                                        await loadOrdersPage();
                                                                        setDropdownOpenId(null);
                                                                    }
                                                                }}
                                                                className="flex w-full items-center gap-3 px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50">
                                                            <TrashIcon className="h-4 w-4" /> Cancel Order
                                                        </button>
                                                    )}
                                                </div>,
                                                document.body
                                            )}
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                        <TablePagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            totalItems={totalItems}
                            itemsPerPage={ITEMS_PER_PAGE}
                            onPageChange={handlePageChange}
                        />
                    </>
                )}
            </div>

            {isCreateDeliveryModalOpen && (
                <CreateDeliveryModal
                    selectedOrder={selectedOrder}
                    isOpen={isCreateDeliveryModalOpen}
                    onClose={() => setIsCreateDeliveryModalOpen(false)}
                    newDelivery={newDelivery}
                    onDeliveryChange={e => setNewDelivery(p => ({ ...p, destination_id: e.target.value }))}
                    onDriverChange={e => setNewDelivery(p => ({ ...p, driver_id: e.target.value }))}
                    onSubmit={handleDeliverySubmit}
                    loading={loading || orderLoading}
                    customerAddresses={addresses}
                />
            )}

            <CreateOrderModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                orderData={orderData}
                newCustomer={newCustomer}
                showNewCustomerForm={showNewCustomerForm}
                customers={customers}
                onOrderChange={handleOrderChange}
                onOrderItemsChange={handleOrderItemsChange}
                onCustomerChange={handleCustomerChange}
                onToggleCustomerForm={() => setShowNewCustomerForm(!showNewCustomerForm)}
                onSubmit={handleSubmit}
                loading={loading || orderLoading}
                stores={stores}
            />
        </div>
    );
};

export default Orders;